import tippy, { Instance, Props } from "tippy.js";
import { css } from "@emotion/css";
import { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";
import scrollIntoView from "smooth-scroll-into-view-if-needed";
import { Editor } from "@tiptap/core";
import {isDarkMode} from "@/utils";

export type BlockMenuViewItem =
  | {
      separator: true;
    }
  | {
      separator?: false;
      view: (dom: HTMLElement) => void;
      [key: string]: any;
    };

export type BlockMenuViewOptions = {
  editor: Editor;
  tippyOptions?: Partial<Props>;
  dictionary: {
    queryEmpty: string;
  };
};

let timer:any = null

export default class BlockMenuView {
  private editor: Editor;
  private dictionary: BlockMenuViewOptions["dictionary"];
  private popup: Instance;
  private dom: HTMLElement;
  private selected: number;
  private items: BlockMenuViewItem[];
  private command: (props: any) => void;
  private isFromKeyboard:boolean;

  constructor(options: BlockMenuViewOptions) {
    this.editor = options.editor;
    this.dictionary = options.dictionary;
    // root
    this.dom = document.createElement("div");
    this.dom.id = `id-${Math.random().toString(36).substring(2, 10)}`;
    this.dom.classList.add(css`
      color: var(--tiptap-color-text);
      background-color: var(--bubble-background-color);
      z-index: 99999;
      border-radius: 8px;
      box-shadow: var(--dialog-box-shadow);
      transition: opacity 150ms cubic-bezier(0.175, 0.885, 0.32, 1.275),
        transform 150ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
      transition-delay: 150ms;
      line-height: 0;
      box-sizing: border-box;
      pointer-events: all;
      white-space: nowrap;
      width: 178px;
      max-height: 324px;
      overflow: hidden;
      overflow-y: auto;
      padding: 0.5em 0;
      border: 1px solid var(--bubble-border-color);

      * {
        box-sizing: border-box;
        //img {
        //  filter: brightness(var(--img-brightness-value));
        //}
      }

      @media print {
        display: none;
      }
    `);


    // popup
    this.popup = tippy(document.body, {
      appendTo: () => document.body,
      getReferenceClientRect: null,
      showOnCreate: true,
      interactive: true,
      trigger: "manual",
      placement: "top-start",
      content: this.dom,
      zIndex:999999,
      ...(options.tippyOptions ?? {}),
    });

    if (!this.editor.isFocused){
      // this.dom.style.opacity = '0';
      this.popup.hide();
    }

    // init
    this.selected = 0;
    this.items = [];
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    this.command = () => {};
  }

  public update(props: SuggestionProps) {
    // pos
    this.popup.setProps({
      getReferenceClientRect: props.clientRect,
    });
    // items
    this.items = props.items;
    this.command = props.command;
    // render
    this.render();
  }

  public render() {
    // console.log('this.editor.isFocused:'+this.editor.isFocused)
    // if (this.editor.isFocused){
    //   this.dom.style.opacity = '1';
    // }
    this.dom.innerHTML = "";

    if (this.items.length > 0) {
      const nodes = this.items.map((item, index) => {
        if (!item.separator) {
          const button = document.createElement("button");
          button.onmouseenter = ()=>{
            if (!this.isFromKeyboard){
              this.selected = index
              if (!timer) {
                timer = setTimeout(() => {
                  this.render();
                  timer = null
                }, 40)
              }
            }
            setTimeout(()=>{
              this.isFromKeyboard = false;
            },1000)

          }
          button.classList.add(css`
            appearance: none;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            font-weight: 500;
            font-size: 12px;
            line-height: 1;
            width: 100%;
            height: 3.0em;
            cursor: pointer;
            border: none;
            outline: none;
            color: var(--text-color);
            padding: 0 1em;

            background-color: ${this.selected === index ? "var(--button-background-color)" : "var(--tiptap-color-background)"};
            color: ${this.selected === index ? "white" : "var(--text-color)"};
            &:hover {
              //background-color: var(--button-background-color);
              //color: white;
            }
          `);
          // button.addEventListener("click", () => {
          //   this.command(item);
          // });
          button.addEventListener('mouseup',()=>{
            this.command(item);
          })
          button.addEventListener('mousedown',(event)=>{
            event.stopPropagation();
          })
          item.view(button);
          return button;
        } else {
          const separator = document.createElement("div");
          separator.classList.add(css`
            border-top: 1px solid var(--tiptap-color-background-hover);
            margin: 0.5em 0;
          `);
          return separator;
        }
      });
      this.dom.append(...nodes);
      // scroll
      if (this.isFromKeyboard){
        scrollIntoView(nodes[this.selected], {
          scrollMode: "if-needed",
          block: "center",
          boundary: (parent) => parent.id !== this.dom.id,
        });
      }

    } else {
      const empty = document.createElement("div");
      empty.classList.add(css`
        display: flex;
        align-items: center;
        color: var(--tiptap-color-text);
        font-weight: 500;
        font-size: 0.8em;
        height: 2.8em;
        padding: 0 1em;
      `);
      empty.textContent = this.dictionary.queryEmpty;
      this.dom.append(empty);
    }
  }

  public onKeyDown(props: SuggestionKeyDownProps): boolean {
    const e = props.event;
    if (e.key === "Escape") {
      this.popup.hide();
      return true;
    }
    if (e.key === "Enter") {
      const item = this.items[this.selected];
      if (item && !item.separator) {
        this.command(item);
      }
      return true;
    }
    if (e.key === "ArrowUp") {
      const prev = this.selected - 1;
      const index =
        this.items[prev] && this.items[prev].separator ? prev - 1 : prev;
      this.selected = index < 0 ? this.items.length - 1 : index;
      this.isFromKeyboard = true;
      this.render();
      return true;
    }
    if (e.key === "ArrowDown") {
      const next = this.selected + 1;
      const index =
        this.items[next] && this.items[next].separator ? next + 1 : next;
      this.selected = index >= this.items.length ? 0 : index;
      this.isFromKeyboard = true;
      this.render();
      return true;
    }
    return false;
  }

  public destroy() {
    this.popup.destroy();
  }
}
